import type { Competition, Me, Role } from '../lib/api';
import { StatusBadge } from './StatusBadge';
import { ActivateCompetitionButton } from './ActivateCompetitionButton';
import { EndCompetitionButton } from './EndCompetitionButton';
import { AdvanceStageButton } from './AdvanceStageButton';

// ─────────────────────────────────────────────────────────────────────────────
// CompetitionLifecycleControls — the ONE cluster of "take this competition
// through its lifecycle" controls: status pill + Activate + End + the
// legacy flat-stage Advance ladder. Extracted from TournamentSetup's anchor
// row (Lisa, 2026-07-19 layout pass) so the Setup page can mount the EXACT
// same controls a second time in the sticky bottom activation bar without
// duplicating markup or wiring two parallel onSuccess handlers — both mount
// points pass the same activeComp/role/me and the same `onChanged` refetch,
// so activating from the bar and activating from the anchor row behave
// identically.
//
// This does NOT own the "Status" label or the highlight/jump-pulse wrapper
// (`registerSection('status-activate')`) — those stay page-specific in the
// anchor row, since the bottom bar isn't a checklist-jump target and doesn't
// need its own registered highlight section.
// ─────────────────────────────────────────────────────────────────────────────

export function CompetitionLifecycleControls({
  activeComp,
  role,
  orgId,
  me,
  hasStagePipeline,
  onChanged,
  className,
}: {
  activeComp: Competition | undefined;
  role?: Role | null;
  /** The comp's OWN parent org — for the org-owner legs on Activate/End/Advance. */
  orgId?: string | null;
  /** effectiveMe — view-as-aware, for the org-owner membership checks. */
  me?: Me | null;
  hasStagePipeline: boolean;
  onChanged: () => void;
  className?: string;
}) {
  return (
    <div className={className}>
      {/* flex-wrap (mobile fix): the badge + Activate + End buttons
          previously sat in a no-wrap flex row that could overflow the
          narrow-viewport container width — with nothing to scroll or clip
          it visibly, Activate ended up off-canvas/unreachable on a phone.
          Wrapping lets the row break onto a 2nd line instead; on desktop
          there's always room for one line, so this is a no-op there. */}
      <div className="flex flex-wrap items-center gap-2 py-1.5">
        {activeComp ? (
          <>
            <StatusBadge status={activeComp.status} />
            <ActivateCompetitionButton
              compId={activeComp.id}
              status={activeComp.status}
              role={role}
              orgId={orgId}
              me={me}
              onActivated={onChanged}
            />
            <EndCompetitionButton
              compId={activeComp.id}
              status={activeComp.status}
              role={role}
              orgId={orgId}
              me={me}
              onEnded={onChanged}
            />
          </>
        ) : (
          <span className="text-xs text-ink-meta">No competition selected</span>
        )}
      </div>
      {activeComp?.status === 'PENDING' && (
        <p className="mt-1 text-xs text-ink-meta">Activating locks the team size.</p>
      )}
      {activeComp && (
        <AdvanceStageButton
          className="mt-2"
          compId={activeComp.id}
          stage={activeComp.stage}
          hasStagePipeline={hasStagePipeline}
          role={role}
          orgId={orgId}
          me={me}
          onAdvanced={onChanged}
        />
      )}
    </div>
  );
}
